import React from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import { webDialogOverlay } from '../utils/webLayout';
import { AppButton } from './AppButton';

type Props = {
  visible: boolean;
  title: string;
  /** Optional detail under the title; omit for a title-only prompt. */
  message?: string;
  confirmText?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * In-app Confirm / Cancel popup. React Native Web implements Alert.alert as a
 * no-op, so destructive actions confirmed through Alert never ran on web; this
 * renders the same on web, iOS and Android.
 */
export function ConfirmDialog({
  visible,
  title,
  message,
  confirmText = 'Confirm',
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  const palette = useAppColors();
  if (!visible) return null;

  function cancel() {
    if (!busy) onCancel();
  }

  return (
    <Modal visible transparent animationType="fade" onRequestClose={cancel}>
      <View style={webDialogOverlay}>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Dismiss"
          style={StyleSheet.absoluteFill}
          onPress={cancel}
        />
        <View
          accessibilityRole="alert"
          style={[
            styles.card,
            { backgroundColor: palette.surface, borderColor: palette.border },
          ]}
        >
          <Text style={[styles.title, { color: palette.textPrimary }]}>{title}</Text>
          {message ? (
            <Text style={[styles.message, { color: palette.textSecondary }]}>{message}</Text>
          ) : null}
          <View style={styles.actions}>
            <AppButton
              title="Cancel"
              variant="secondary"
              disabled={busy}
              onPress={cancel}
            />
            <AppButton
              title={confirmText}
              variant={destructive ? 'danger' : 'primary'}
              loading={busy}
              onPress={onConfirm}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  card: {
    width: '100%',
    maxWidth: 420,
    borderWidth: 1,
    borderRadius: 16,
    padding: spacing.xl,
    gap: spacing.md,
  },
  title: { ...typography.sectionTitle },
  message: { ...typography.body },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
});
